import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../contexts/GoalsContext';

export default function NewGoalSheet({ isOpen, onClose }) {
  const { currentUser } = useAuth();
  const { addGoal } = useGoals();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    targetDate: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { name: 'Wellness', icon: '🌱' },
    { name: 'Learning', icon: '📚' },
    { name: 'Career', icon: '💼' },
    { name: 'Creativity', icon: '🎨' },
    { name: 'Fitness', icon: '💪' },
    { name: 'Mindfulness', icon: '🧘' },
  ];

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setFormData(prev => ({
      ...prev,
      category: category.name
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.category || !formData.targetDate) {
      // Show error toast or validation message
      return;
    }

    setIsSubmitting(true);
    try {
      const newGoal = {
        ...formData,
        userId: currentUser.uid,
        progress: 0,
        status: 'active'
      };

      await addGoal(newGoal);
      
      // Reset form and close
      setFormData({
        title: '',
        category: '',
        targetDate: '',
        description: ''
      });
      setSelectedCategory(null);
      onClose();
    } catch (error) {
      console.error('Error creating goal:', error);
      // Show error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="flex items-end min-h-full">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="w-full h-[85vh] transform overflow-hidden bg-white rounded-t-2xl shadow-xl transition-all">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                  {/* Drag Handle */}
                  <div className="flex justify-center py-2">
                    <div className="w-12 h-1.5 rounded-full bg-gray-300" />
                  </div>
                  <div className="flex items-center justify-between px-4 pb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      New Goal
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="px-4 py-6 space-y-6 overflow-y-auto" style={{ height: 'calc(85vh - 76px)' }}>
                  {/* Goal Title */}
                  <div className="space-y-2">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      What's your goal?
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Learn to play guitar"
                    />
                  </div>

                  {/* Category Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {categories.map((category) => (
                        <button
                          key={category.name}
                          onClick={() => handleCategorySelect(category)}
                          className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors
                            ${selectedCategory?.name === category.name 
                              ? 'border-primary-500 bg-primary-50' 
                              : 'border-transparent hover:border-primary-500 hover:bg-primary-50'
                            }`}
                        >
                          <span className="text-2xl">{category.icon}</span>
                          <span className="text-sm font-medium text-gray-600">{category.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Target Date */}
                  <div className="space-y-2">
                    <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700">
                      Target Date
                    </label>
                    <input
                      type="date"
                      id="targetDate"
                      value={formData.targetDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description (Optional)
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add any notes or details about your goal..."
                    />
                  </div>

                  {/* Create Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Goal'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 